"use client";

import React from "react";
import { SlidersHorizontal, ShieldCheck, ArrowRight, CircleCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON, type L6 } from "@/lib/commonCopy";
interface SpecItem {
  label: L6;
  value: string;
}
const specifications: SpecItem[] = [{
  label: {
    en: "Woven wire mesh",
    zh: "编织金属网",
    es: "Malla de alambre tejida",
    de: "Drahtgewebe",
    fr: "Toile métallique tissée",
    pt: "Malha de arame tecida"
  },
  value: "1–635 mesh"
}, {
  label: {
    en: "Dutch weave",
    zh: "密纹荷兰织",
    es: "Malla holandesa",
    de: "Tressengewebe",
    fr: "Toile hollandaise",
    pt: "Malha holandesa"
  },
  value: "8x85 – 400x2800"
}, {
  label: {
    en: "Sintered mesh",
    zh: "烧结多层网",
    es: "Malla sinterizada",
    de: "Sintergewebe",
    fr: "Toile frittée",
    pt: "Malha sinterizada"
  },
  value: "1–200 µm"
}, {
  label: {
    en: "Alloy grades",
    zh: "标准合金材质",
    es: "Aleaciones estándar",
    de: "Standardlegierungen",
    fr: "Alliages standards",
    pt: "Ligas padrão"
  },
  value: "304 / 316L / 310S"
}];
const copy = {
  eyebrow: {
    en: "Polymer & Extrusion Filtration",
    zh: "聚合物与挤出过滤",
    es: "Filtración de polímeros y extrusión",
    de: "Polymer- & Extrusionsfiltration",
    fr: "Filtration de polymères et d'extrusion",
    pt: "Filtração de polímeros e extrusão"
  },
  standardTag: {
    en: "ISO 9044 / ASTM E2016",
    zh: "ISO 9044 / ASTM E2016",
    es: "ISO 9044 / ASTM E2016",
    de: "ISO 9044 / ASTM E2016",
    fr: "ISO 9044 / ASTM E2016",
    pt: "ISO 9044 / ASTM E2016"
  },
  h1: {
    en: "Engineered mesh for polymer melt filtration",
    zh: "高分子聚合物熔体过滤专用金属网",
    es: "Malla técnica para filtración de polímeros fundidos",
    de: "Präzisions-Drahtgewebe für die Polymerschmelzefiltration",
    fr: "Toiles métalliques pour la filtration des polymères en fusion",
    pt: "Malha técnica para filtração de polímeros fundidos"
  },
  lead: {
    en: "Maintain steady melt flow and particle retention under high extrusion pressures. We supply precision stainless steel woven wire weaves, sintered laminates, and framed screen packs engineered for film casting, fiber spinning, and resin compounding.",
    zh: "在高挤出压力下保持平稳熔体流速与颗粒拦截。MutualFilter 制造精密不锈钢编织丝网、烧结复合网及包边滤网包，专用于吹膜、流延薄膜、化纤纺丝与工程塑料改性造粒。",
    es: "Mantenga un flujo homogéneo de masa fundida y una retención eficaz bajo altas presiones de extrusión. Suministramos mallas tejidas de acero inoxidable, laminados sinterizados y paquetes de discos filtrantes.",
    de: "Gleichmäßiger Schmelzefluss und definierte Partikelrückhaltung unter extremen Extrusionsdrücken. Wir liefern Präzisions-Drahtgewebe aus Edelstahl, Sintermetall-Laminate und gerahmte Siebpakete für Blasfolien, Faserspinnen und Granulierung.",
    fr: "Maintenez un écoulement stable du polymère fondu et une rétention particulaire nette sous hautes pressions d'extrusion. Nous fournissons des toiles tissées en acier inoxydable, des laminés frittés et des packs filtrants pour l'extrusion et le filage.",
    pt: "Mantenha a vazão estável e retenção precisa de partículas sob altas pressões de extrusão. Fornecemos malhas tecidas em aço inoxidável, laminados sinterizados e pacotes de filtros para extrusão e fiação."
  },
  secondaryCta: {
    en: "Polymer filtration overview",
    zh: "聚合物过滤概览",
    es: "Descripción de filtración de polímeros",
    de: "Übersicht Polymerfiltration",
    fr: "Aperçu filtration des polymères",
    pt: "Visão geral de filtração de polímeros"
  },
  imageAlt: {
    en: "Precision stainless steel woven wire mesh for high pressure polymer melt filtration",
    zh: "高压聚合物熔体过滤专用精密不锈钢编织丝网与滤网包",
    es: "Malla de alambre de acero inoxidable para filtración de polímeros fundidos a alta presión",
    de: "Präzisions-Edelstahlgewebe für Hochdruck-Polymerschmelzefiltration",
    fr: "Toile métallique de précision en acier inoxydable pour filtration de polymères sous haute pression",
    pt: "Malha de arame em aço inoxidável para filtração de polímeros sob alta pressão"
  },
  overlayBadge: {
    en: "High Differential Resistance",
    zh: "耐受高工作压差",
    es: "Alta resistencia a presión diferencial",
    de: "Hohe Differenzdruckfestigkeit",
    fr: "Résistance élevée au différentiel de pression",
    pt: "Resistência a alta pressão diferencial"
  },
  cardFooterNote: {
    en: "Screen changers & continuous extrusion",
    zh: "适配换网器与连续挤出系统",
    es: "Cambiadores de filtros y extrusión continua",
    de: "Siebwechsler & kontinuierliche Extrusion",
    fr: "Changeurs de filtres et extrusion continue",
    pt: "Trocadores de telas e extrusão contínua"
  },
  cardFooterLink: {
    en: "OEM Custom",
    zh: "OEM定制",
    es: "OEM personalizado",
    de: "OEM-Kundenspezifisch",
    fr: "OEM sur mesure",
    pt: "OEM personalizado"
  }
};
export default function PolymerHero() {
  const {
    lang
  } = useLanguage();
  return <section id="polymermelthero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
          {/* Left Column: Badges, Headline, Lead & CTAs (lg:col-span-7) */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <Reveal>
              {/* Badges row: exactly two outline badges */}
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption text-foreground">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{pick(lang, copy.eyebrow)}</span>
                </Badge>
                <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-2.5 py-1 font-mono mf-caption text-foreground">
                  <ShieldCheck className="size-3.5 text-primary inline" aria-hidden="true" />
                  <span>{pick(lang, copy.standardTag)}</span>
                </Badge>
              </div>

              {/* Page h1 */}
              <h1 className="font-default text-balance mf-h1 text-foreground">
                {pick(lang, copy.h1)}
              </h1>

              {/* Lead paragraph (2-3 sentences) */}
              <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
                {pick(lang, copy.lead)}
              </p>

              {/* CTAs */}
              <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
                <Button size="lg" asChild className="px-6 text-sm font-semibold transition-colors w-full sm:w-auto">
                  <Link to="/RequestAQuotePage" className="inline-flex items-center justify-center gap-2">
                    <span>{pick(lang, COMMON.requestQuote)}</span>
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </Button>

                <Button variant="outline" size="lg" asChild className="px-6 text-sm font-semibold transition-colors w-full sm:w-auto">
                  <Link to="/PolymerFiltrationPage" className="inline-flex items-center justify-center gap-2">
                    <span>{pick(lang, copy.secondaryCta)}</span>
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </Button>
              </div>
            </Reveal>
          </div>

          {/* Right Column: ONE bordered Card (lg:col-span-5) */}
          <div className="lg:col-span-5 w-full">
            <Reveal delay={60}>
              <Card className="overflow-hidden rounded-lg border border-border bg-card text-card-foreground gap-0 py-0">
                <div className="relative mf-card-media mf-ratio-hero w-full">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-1e6ef7.png" alt={pick(lang, copy.imageAlt)} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover" />
                  <div className="absolute top-3 left-3 bg-card border border-border px-2.5 py-1 rounded-md mf-caption font-semibold text-foreground flex items-center gap-1.5">
                    <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                    <span>{pick(lang, copy.overlayBadge)}</span>
                  </div>
                </div>

                <CardContent className="p-4 bg-card">
                  <div className="flex items-center justify-between mf-caption">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <CircleCheck className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                      <span>{pick(lang, copy.cardFooterNote)}</span>
                    </div>
                    <Link to="/OEMCustomMeshEngineeringPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 inline-flex items-center gap-1 shrink-0">
                      <span>{pick(lang, copy.cardFooterLink)}</span>
                      <ArrowRight className="size-3" aria-hidden="true" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </div>

        {/* 4-Item Spec Strip under the grid */}
        <div className="mf-spec-strip">
          {specifications.map((spec, i) => <div key={i} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">
                {pick(lang, spec.label)}
              </span>
              <span className="mf-spec-strip__value font-mono">
                {spec.value}
              </span>
            </div>)}
        </div>
      </div>
    </section>;
}